(() => {
  if (customElements.get('mystoy-dialog')) return;

  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  class MystoyElement extends HTMLElement {
    connectedCallback() { this.init(); }
    disconnectedCallback() { this.destroy(); }
    init() {
      this.destroy();
      this.controller = new AbortController();
    }
    listen(target, type, handler) {
      target?.addEventListener(type, handler, { signal: this.controller.signal });
    }
    destroy() { this.controller?.abort(); }
  }

  class MystoyDisclosure extends MystoyElement {
    init() {
      super.init();
      this.details = this.querySelector('details');
      this.summary = this.details?.querySelector('summary');
      if (!this.details || !this.summary) return;
      this.listen(this, 'keydown', (event) => {
        if (event.key !== 'Escape' || !this.details.open) return;
        event.preventDefault();
        this.details.open = false;
        this.summary.focus();
      });
      this.listen(document, 'click', (event) => {
        if (!this.contains(event.target)) this.details.open = false;
      });
      this.listen(this, 'focusout', (event) => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.details.open = false;
      });
    }
    destroy() {
      super.destroy();
      if (this.details) this.details.open = false;
    }
  }

  class MystoyDialog extends MystoyElement {
    init() {
      super.init();
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || !this.dialog.showModal) return;
      this.listen(document, 'click', (event) => {
        const opener = event.target.closest('[data-dialog-open]');
        if (!opener) return;
        const target = opener.dataset.dialogOpen || opener.getAttribute('aria-controls');
        if (target ? target !== this.dialog.id : !this.contains(opener)) return;
        event.preventDefault();
        this.open(opener);
      });
      this.listen(this.dialog, 'click', (event) => {
        if (event.target.closest('[data-dialog-close]')) {
          event.preventDefault();
          this.close();
        } else if (event.target === this.dialog) {
          const bounds = this.dialog.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.close();
        }
      });
      this.listen(this.dialog, 'cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.listen(this.dialog, 'close', () => {
        if (!this.dialog.open) this.restoreFocus();
      });
      this.listen(this.dialog, 'keydown', (event) => {
        if (event.defaultPrevented) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
        }
        if (event.key !== 'Tab') return;
        const items = [...this.dialog.querySelectorAll(focusableSelector)]
          .filter((element) => element.tabIndex >= 0 && !element.matches(':disabled') && element.getClientRects().length && getComputedStyle(element).visibility === 'visible' && !element.closest('[inert]'));
        const first = items[0];
        const last = items.at(-1);
        if (!first) {
          event.preventDefault();
          this.dialog.focus();
        } else if (event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === this.dialog)) {
          event.preventDefault();
          first.focus();
        }
      });
    }
    open(opener = document.activeElement) {
      if (!this.dialog || this.dialog.open) return;
      this.opener = opener;
      this.dialog.showModal();
    }
    close() {
      if (!this.dialog?.open) return;
      this.dialog.close();
      this.restoreFocus();
    }
    restoreFocus() {
      const opener = this.opener;
      this.opener = null;
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    }
    destroy() {
      super.destroy();
      this.close();
    }
  }

  class MystoyReveal extends MystoyElement {
    init() {
      super.init();
      const reveal = () => {
        this.removeAttribute('data-reveal-pending');
        this.observer?.disconnect();
      };
      this.listen(reducedMotion, 'change', (event) => { if (event.matches) reveal(); });
      this.listen(this, 'focusin', reveal);
      if (reducedMotion.matches || !('IntersectionObserver' in window) || window.Shopify?.designMode || this.hasAttribute('data-revealed')) return;
      this.setAttribute('data-reveal-pending', '');
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.setAttribute('data-revealed', '');
        reveal();
      }, { rootMargin: '0px 0px 32px 0px', threshold: 0 });
      this.observer.observe(this);
    }
    destroy() {
      super.destroy();
      this.observer?.disconnect();
      this.removeAttribute('data-reveal-pending');
    }
  }

  class MystoyQuantity extends MystoyElement {
    init() {
      super.init();
      this.input = this.querySelector('input[type="number"]');
      if (!this.input) return;
      this.minus = this.querySelector('[data-quantity-minus]');
      this.plus = this.querySelector('[data-quantity-plus]');
      this.listen(this, 'click', (event) => {
        const button = event.target.closest('[data-quantity-minus], [data-quantity-plus]');
        if (!button || !this.contains(button)) return;
        event.preventDefault();
        if (this.input.disabled || this.input.readOnly || button.disabled) return;
        const previous = this.input.value;
        if (this.input.step === 'any') return;
        if (button.hasAttribute('data-quantity-minus')) this.input.stepDown();
        else this.input.stepUp();
        if (this.input.value !== previous) {
          this.input.dispatchEvent(new Event('input', { bubbles: true }));
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.update();
      });
      this.listen(this.input, 'input', () => this.update());
      this.listen(this.input, 'change', () => this.update());
      this.observer = new MutationObserver(() => this.update());
      this.observer.observe(this.input, { attributes: true, attributeFilter: ['min', 'max', 'step', 'disabled', 'readonly', 'value'] });
      this.update();
    }
    update() {
      const value = this.input.valueAsNumber;
      const disabled = this.input.disabled || this.input.readOnly;
      if (this.minus) this.minus.disabled = disabled || (this.input.min !== '' && value <= Number(this.input.min));
      if (this.plus) this.plus.disabled = disabled || (this.input.max !== '' && value >= Number(this.input.max));
    }
    destroy() {
      super.destroy();
      this.observer?.disconnect();
    }
  }

  class MystoyVariants extends MystoyElement {
    init() {
      super.init();
      try {
        this.data = JSON.parse(this.querySelector('[data-variant-data]')?.textContent || '[]');
      } catch (_) { this.data = []; }
      this.selectors = [...this.querySelectorAll('[data-option-selector]')];
      this.idInput = this.querySelector('[data-variant-id]');
      this.priceEl = this.querySelector('[data-price-current]');
      this.compareEl = this.querySelector('[data-price] s');
      this.addButton = this.querySelector('[data-add-button]');
      this.addLabel = this.querySelector('[data-add-label]');
      const scope = this.closest('.product-page') || document;
      this.stickyPrice = scope.querySelector('[data-sticky-price]');
      this.stickyAdd = scope.querySelector('[data-sticky-add]');
      this.addText = this.dataset.addText || 'Add to cart';
      this.soldoutText = this.dataset.soldoutText || 'Sold out';
      this.selectors.forEach((sel) => this.listen(sel, 'change', () => this.update()));
      if (this.data.length) this.update();
    }
    current() { return this.selectors.map((s) => s.value); }
    match() {
      const opts = this.current();
      return this.data.find((v) => Array.isArray(v.options) && v.options.length === opts.length && v.options.every((o, i) => String(o) === String(opts[i])));
    }
    update() {
      const v = this.match();
      if (!v) return;
      if (this.idInput) this.idInput.value = v.id;
      if (this.priceEl) this.priceEl.textContent = v.price_formatted;
      if (this.stickyPrice) this.stickyPrice.textContent = v.price_formatted;
      if (this.compareEl) {
        if (v.compare_at_formatted) { this.compareEl.textContent = v.compare_at_formatted; this.compareEl.hidden = false; }
        else { this.compareEl.hidden = true; }
      }
      const soldOut = !v.available;
      for (const btn of [this.addButton, this.stickyAdd]) { if (btn) btn.disabled = soldOut; }
      if (this.addLabel) this.addLabel.textContent = soldOut ? this.soldoutText : this.addText;
      if (this.stickyAdd) this.stickyAdd.textContent = soldOut ? this.soldoutText : this.addText;
    }
  }

  for (const [name, component] of [
    ['mystoy-dialog', MystoyDialog],
    ['mystoy-disclosure', MystoyDisclosure],
    ['mystoy-reveal', MystoyReveal],
    ['mystoy-quantity', MystoyQuantity],
    ['mystoy-variants', MystoyVariants],
  ]) {
    if (!customElements.get(name)) customElements.define(name, component);
  }

  const selector = 'mystoy-dialog, mystoy-disclosure, mystoy-reveal, mystoy-quantity';
  for (const [eventName, method] of [['shopify:section:load', 'init'], ['shopify:section:unload', 'destroy']]) {
    document.addEventListener(eventName, (event) => {
      const elements = [...event.target.querySelectorAll(selector)];
      if (event.target.matches(selector)) elements.unshift(event.target);
      elements.forEach((element) => element[method]());
    });
  }

  // Add a hairline to the sticky header once the page is scrolled.
  const header = document.querySelector('[data-site-header]');
  if (header) {
    const setScrolled = () => header.toggleAttribute('data-scrolled', window.scrollY > 8);
    setScrolled();
    addEventListener('scroll', setScrolled, { passive: true });
  }

  // Gentle pointer + scroll parallax on the hero media (motion-safe).
  const heroMedia = document.querySelector('[data-hero-media]');
  if (heroMedia && !reducedMotion.matches) {
    const hero = heroMedia.closest('[data-hero]') || heroMedia;
    const items = [...heroMedia.querySelectorAll('[data-parallax]')];
    let mx = 0, my = 0, sy = 0, raf = 0;
    const apply = () => {
      raf = 0;
      for (const el of items) {
        const f = parseFloat(el.dataset.parallax) || 0;
        el.style.setProperty('--px', (mx * f * 130).toFixed(1) + 'px');
        el.style.setProperty('--py', (my * f * 130 - sy * f * 0.35).toFixed(1) + 'px');
      }
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(apply); };
    hero.addEventListener('pointermove', (event) => {
      if (event.pointerType === 'touch') return;
      const r = hero.getBoundingClientRect();
      mx = (event.clientX - r.left) / r.width - 0.5;
      my = (event.clientY - r.top) / r.height - 0.5;
      schedule();
    }, { passive: true });
    hero.addEventListener('pointerleave', () => { mx = 0; my = 0; schedule(); });
    addEventListener('scroll', () => { sy = window.scrollY; schedule(); }, { passive: true });
  }

  // Ajax add-to-cart: add without leaving the page and open the cart drawer.
  const drawerEl = document.querySelector('mystoy-dialog.cart-drawer');
  if (drawerEl && window.fetch) {
    const bumpCount = () => {
      const counts = document.querySelectorAll('[data-cart-count]');
      counts.forEach((c) => { c.classList.remove('is-bump'); void c.offsetWidth; c.classList.add('is-bump'); });
    };

    const refreshDrawer = async () => {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=header`, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('section fetch failed');
      const data = await res.json();
      const html = data.header;
      if (!html) throw new Error('no header section');
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newDrawer = doc.querySelector('#MystoyCartDrawer');
      const curDrawer = document.querySelector('#MystoyCartDrawer');
      if (newDrawer && curDrawer) curDrawer.innerHTML = newDrawer.innerHTML;
      const newCount = doc.querySelector('[data-cart-count]');
      document.querySelectorAll('[data-cart-count]').forEach((c) => {
        if (newCount) { c.textContent = newCount.textContent; c.toggleAttribute('hidden', newCount.hasAttribute('hidden')); }
      });
      bumpCount();
    };

    const openDrawer = () => {
      const inst = document.querySelector('mystoy-dialog.cart-drawer');
      if (inst && typeof inst.open === 'function') inst.open();
    };

    document.addEventListener('submit', async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const action = form.getAttribute('action') || '';
      if (!action.includes('/cart/add')) return;
      event.preventDefault();

      const submitters = [...form.querySelectorAll('[data-add-button], [data-add-label]')];
      const triggered = [form.querySelector('[data-add-button]'), document.querySelector('[data-sticky-add]')].filter(Boolean);
      triggered.forEach((b) => b.setAttribute('aria-busy', 'true'));

      try {
        const body = new FormData(form);
        const addRes = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        if (!addRes.ok) {
          let msg = '';
          try { msg = (await addRes.json()).description || ''; } catch (_) {}
          throw new Error(msg || 'add failed');
        }
        await refreshDrawer();
        openDrawer();
      } catch (err) {
        // Fall back to the standard cart page if anything goes wrong.
        window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
      } finally {
        triggered.forEach((b) => b.removeAttribute('aria-busy'));
        void submitters;
      }
    });
  }
})();
